import type { ReactNode } from 'react';
import { Chip, Paper, Slider, Stack, Typography } from '@mui/material';

import type { SpeechCalibrationTrait } from '../admin/adminSpeechCalibrationApi';
import {
    calibrationTraitLabels,
    statusColor,
} from './speechCalibrationConstants';

export function StatusChip({ status }: { status: string }) {
    return (
        <Chip
            size="small"
            color={statusColor(status)}
            variant="outlined"
            label={status.replaceAll('_', ' ')}
            sx={{ textTransform: 'capitalize', fontWeight: 700 }}
        />
    );
}

export function MetricCard({
    label,
    value,
    helper,
}: {
    label: string;
    value: ReactNode;
    helper?: string;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                minWidth: 0,
                p: 2,
                borderRadius: 2.5,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            }}
        >
            <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 850, textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography sx={{ mt: 0.5, color: '#0f172a', fontSize: '1.5rem', fontWeight: 850 }}>
                {value}
            </Typography>
            {helper ? <Typography sx={{ mt: 0.25, color: '#94a3b8', fontSize: '0.75rem' }}>{helper}</Typography> : null}
        </Paper>
    );
}

export function TraitScoreSlider({
    trait,
    value,
    onChange,
    disabled = false,
}: {
    trait: SpeechCalibrationTrait;
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
}) {
    return (
        <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 800 }}>{calibrationTraitLabels[trait]}</Typography>
                <Chip size="small" label={`${value.toFixed(1)} / 5`} color="primary" sx={{ fontWeight: 850 }} />
            </Stack>
            <Slider
                aria-label={`${calibrationTraitLabels[trait]} rating`}
                min={0}
                max={5}
                step={0.5}
                marks={[
                    { value: 0, label: '0' },
                    { value: 1, label: '1' },
                    { value: 2, label: '2' },
                    { value: 3, label: '3' },
                    { value: 4, label: '4' },
                    { value: 5, label: '5' },
                ]}
                value={value}
                disabled={disabled}
                onChange={(_, nextValue) => onChange(nextValue as number)}
                valueLabelDisplay="auto"
            />
        </Stack>
    );
}
